import { Info, Sparkle, WarningCircle } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
} from "react";

const alertVariants = cva("min-w-0 p-1 rounded-3xl border", {
  variants: {
    variant: {
      default: "border-black-200 bg-white shadow-lg",
      warning: "border-yellow-400 bg-white shadow-lg",
      danger: "border-red-400 bg-black-50 shadow-lg",
      gradient:
        "border-purple-50 bg-[url(/images/free-trial-banner-bg.png)] bg-cover shadow-[0_0_0_0.5px_rgba(69,21,190,0.20)]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const innerContentClassName = cva(
  "relative rounded-2xl px-1.5 py-1.5 flex gap-x-1.5 [&_[data-slot='icon']]:bg-black-100 text-xs [&_p]:mt-1",
  {
    variants: {
      variant: {
        default: "bg-white shadow-md",
        warning: "bg-white shadow-md",
        danger: "bg-white shadow-md",
        gradient:
          "bg-[url(/images/free-trial-banner-bg.png)] bg-cover shadow-[0_2px_2px_0_rgba(100,44,255,0.16),0_0_0_0.6px_rgba(69,21,190,0.20)] [&_[data-slot='icon']]:bg-purple-100 [&_[data-slot='icon']]:text-purple-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type AlertProps = ComponentPropsWithoutRef<"div"> &
  VariantProps<typeof alertVariants>;

function Alert(
  { variant, className, children, ...props }: AlertProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  let Icon = WarningCircle;

  if (variant === "gradient") {
    Icon = Sparkle;
  } else if (variant === "default") {
    Icon = Info;
  }
  return (
    <div ref={ref} className={alertVariants({ variant, className })} {...props}>
      <div className={innerContentClassName({ variant })}>
        <div
          data-slot="icon"
          className="size-6 shrink-0 rounded-full flex items-center justify-center"
        >
          <Icon weight="fill" size={16} />
        </div>

        <p className="w-full">{children}</p>
      </div>
    </div>
  );
}

Alert.displayName = "Alert";

export default forwardRef(Alert);
